import { api } from "./dataService";

/**
 * XLSX-Objekt-Import für einen Vertrag (Vorschau oder Übernahme), ACC-318.
 * Vorschau (dryRun: true, Default im Backend) berechnet nur, was der Import
 * bewirken würde (to_add/to_update/unchanged/unmatched/to_remove), ohne zu
 * persistieren; dryRun: false führt den Import tatsächlich aus. sync
 * (Default false, additiv) entfernt bei dryRun:false zusätzlich die
 * Vertrags-Verknüpfung aller beim Vertrag hängenden, aber in der Datei
 * fehlenden Objekte (siehe docs/plan/objekt-import-synchron-entfernen.md).
 *
 * @param {number|string} vertragId - interne Vertrags-ID
 * @param {string} filedata - Base64-Data-URL der Datei (liefert DnDFileUpload bereits fertig).
 * @param {{dryRun: boolean, sync?: boolean}} options
 * @returns {Promise<{vertrag: {vertragsnummer: string, kunde_name: string}|null, to_add: Array, to_update: Array, unchanged: Array, unmatched: Array, to_remove: Array, applied: boolean}|object>}
 *          dryRun: true -> Vorschau-Objekt (inkl. Kundenname/Vertragsnummer des Ziel-Vertrags, ACC-321);
 *          dryRun: false -> der aktualisierte Vertrag.
 */
export const importObjektliste = async (
  vertragId,
  filedata,
  { dryRun, sync = false }
) => {
  const response = await api.post("data/objekts/upload", {
    vertrag_id: vertragId,
    api_version: "v2",
    dry_run: dryRun,
    sync,
    objektliste: [{ filedata }],
  });

  // Die Vorschau (dry_run) ist unter "data" gewrapped, die eigentliche
  // Übernahme liefert den aktualisierten Vertrag ungewrapped (siehe
  // ObjektController::upload_objektliste()).
  return dryRun ? response.data.data : response.data;
};

/**
 * Lädt die Beispieldatei mit dem von ObjektlisteRowMapper erwarteten
 * Spaltenformat herunter (ObjektController::downloadObjektlisteTemplate()).
 */
export const downloadObjektlisteTemplate = async () => {
  const response = await api.get("data/objekts/upload/template", {
    responseType: "blob",
    headers: {
      Accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  });

  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = "objektliste_vorlage.xlsx";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Lädt die aktuellen Objekte (inkl. gültiger Sparten) eines Vertrags im selben
 * Spaltenformat wie die Beispieldatei herunter (ACC-321, Nachtrag) -
 * ObjektController::downloadCurrentObjektliste() - damit ein Sachbearbeiter
 * den Bestand bearbeiten und unverändert wieder hochladen kann.
 *
 * @param {number|string} vertragId - interne Vertrags-ID
 * @param {number|string} [fileLabel] - Teil des Dateinamens, Default die Vertrags-ID
 */
export const downloadCurrentObjektliste = async (
  vertragId,
  fileLabel = vertragId
) => {
  const response = await api.get(
    `data/objekts/upload/current/${vertragId}?id_type=internal`,
    {
      responseType: "blob",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
    }
  );

  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = `objektliste_vertrag_${fileLabel}.xlsx`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(downloadUrl);
};
